import { Fragment } from 'react';
import type { DashboardSummary } from '@/types/dashboard';
import { formatInr } from '@/utils/currency';
import { growthHint } from '../utils/growthHint';
import styles from '../Dashboard.module.scss';

interface DashboardSnapshotStripProps {
  summary: DashboardSummary;
  showRevenue?: boolean;
  showOrders?: boolean;
  showCustomers?: boolean;
  showAov?: boolean;
}

export function DashboardSnapshotStrip({
  summary,
  showRevenue = true,
  showOrders = true,
  showCustomers = true,
  showAov = true,
}: DashboardSnapshotStripProps) {
  const items = [
    showRevenue
      ? {
          key: 'revenue',
          label: 'Revenue',
          value: formatInr(summary.revenue),
          hint: growthHint(summary.revenueGrowth),
        }
      : null,
    showOrders
      ? {
          key: 'orders',
          label: 'Orders',
          value: String(summary.orders),
          hint: growthHint(summary.ordersGrowth),
        }
      : null,
    showCustomers
      ? {
          key: 'customers',
          label: 'Customers',
          value: String(summary.totalCustomers),
          hint: `${summary.newCustomers} new`,
        }
      : null,
    showAov
      ? {
          key: 'aov',
          label: 'AOV',
          value: formatInr(summary.averageOrderValue),
          hint: `${summary.itemsSold} items`,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; value: string; hint: string }>;

  if (!items.length) return null;

  return (
    <section className={styles.snapshotStrip}>
      {items.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 ? <div className={styles.snapshotDivider} /> : null}
          <div className={styles.snapshotItem}>
            <span className={styles.snapshotLabel}>{item.label}</span>
            <strong className={styles.snapshotValue}>{item.value}</strong>
            <span className={styles.snapshotHint}>{item.hint}</span>
          </div>
        </Fragment>
      ))}
    </section>
  );
}
